import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'

const C = {
  black: "#0D0D0D", white: "#FAFAF8", orange: "#E8622A",
  orangeFaint: "#FDF0E8", ink: "#1A1A1A", inkLight: "#777",
  inkFaint: "#AAAAAA", border: "#E8E4DE", surface: "#F2EEE8",
}

const FONT = { display: "'Bebas Neue', sans-serif", body: "'DM Sans', sans-serif" }

const PHASES = ['Early Years', 'Primary', 'Secondary', 'Further Education', 'Higher Education', 'Other']
const SUBJECTS = ['Maths', 'English', 'Science', 'History', 'Geography', 'Art', 'Music', 'PE', 'Languages', 'Computing', 'Design & Technology', 'Religious Studies', 'PSHE', 'Drama', 'Business Studies', 'Psychology', 'Sociology', 'Economics', 'Biology', 'Chemistry', 'Physics', 'Leadership', 'Research Methods', 'Academic Writing', 'Lecturing', 'Curriculum Design', 'Teacher Training / PGCE', 'SEND / Special Educational Needs', 'Mentoring', 'Educational Psychology', 'Other']
const AVATAR_COLORS = ['#1A1A1A', '#E8622A', '#7C3AED', '#0D7C66', '#C9952A', '#DC2626', '#0369A1', '#4D7C0F']

export default function SetupProfile() {
  const { profile, updateProfile } = useAuth()
  const [step, setStep] = useState(1)
  const [error, setError] = useState('')
  const [data, setData] = useState({
    name: profile?.name || '',
    role: '', school: '', country: '',
    phase: '', subjects: [],
    years_exp: '', bio: '',
    avatar_color: '#E8622A',
  })
  const [saving, setSaving] = useState(false)

  const update = (key, val) => setData(d => ({ ...d, [key]: val }))
  const toggleSubject = (s) => update('subjects', data.subjects.includes(s) ? data.subjects.filter(x => x !== s) : [...data.subjects, s])

  const save = async () => {
    setSaving(true)
    setError('')
    const { error } = await updateProfile({
      ...data,
      years_exp: parseInt(data.years_exp) || 0
    })
    if (error) setError('Something went wrong — please try again.')
    setSaving(false)
  }

  const inputStyle = {
    width: '100%', border: `1px solid ${C.border}`,
    padding: '13px 14px', fontSize: 15,
    fontFamily: FONT.body, outline: 'none',
    color: C.ink, background: C.surface,
    boxSizing: 'border-box', borderRadius: 0,
    WebkitAppearance: 'none',
  }

  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: C.inkLight,
    display: 'block', marginBottom: 6,
    fontFamily: FONT.body, letterSpacing: '0.08em',
    textTransform: 'uppercase',
  }

  const initials = data.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  const STEP_TITLES = ['ABOUT YOU', 'WHAT YOU TEACH', 'YOUR PROFILE']

  return (
    <div style={{ minHeight: '100vh', background: C.white, fontFamily: FONT.body }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: C.black, padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ width: 8, height: 8, background: C.orange, borderRadius: '50%' }} />
        <span style={{ fontFamily: FONT.display, fontSize: 22, color: C.white, letterSpacing: '0.14em' }}>EDIFYI</span>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 20px 60px' }}>

        {/* Progress */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: FONT.display, fontSize: 36, color: C.ink, letterSpacing: '0.04em', lineHeight: 1, marginBottom: 12 }}>
            {STEP_TITLES[step - 1]}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{ flex: 1, height: 3, background: s <= step ? C.orange : C.border, transition: 'background 0.3s' }} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.inkFaint, fontFamily: FONT.body, marginTop: 6, letterSpacing: '0.06em' }}>
            STEP {step} OF 3
          </div>
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Your name', key: 'name', placeholder: 'e.g. Alex Thompson' },
              { label: 'Job title', key: 'role', placeholder: 'e.g. Head of History, Primary Teacher' },
              { label: 'School or institution', key: 'school', placeholder: 'e.g. Greenwood Academy' },
              { label: 'Country', key: 'country', placeholder: 'e.g. United Kingdom' },
              { label: 'Years of experience', key: 'years_exp', placeholder: 'e.g. 5', type: 'number' },
            ].map(({ label, key, placeholder, type }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input type={type || 'text'} value={data[key]} onChange={e => update(key, e.target.value)} placeholder={placeholder} style={inputStyle} />
              </div>
            ))}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div>
              <label style={labelStyle}>Phase</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PHASES.map(p => (
                  <button key={p} onClick={() => update('phase', p)} style={{ padding: '9px 16px', border: `1px solid ${data.phase === p ? C.orange : C.border}`, background: data.phase === p ? C.orangeFaint : 'transparent', color: data.phase === p ? C.orange : C.inkLight, fontWeight: 700, fontSize: 13, fontFamily: FONT.body, cursor: 'pointer', borderRadius: 0 }}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Subjects — pick all that apply</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {SUBJECTS.map(s => (
                  <button key={s} onClick={() => toggleSubject(s)} style={{ padding: '8px 14px', border: `1px solid ${data.subjects.includes(s) ? C.orange : C.border}`, background: data.subjects.includes(s) ? C.orangeFaint : 'transparent', color: data.subjects.includes(s) ? C.orange : C.inkLight, fontWeight: 700, fontSize: 12, fontFamily: FONT.body, cursor: 'pointer', borderRadius: 0 }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={labelStyle}>Short bio</label>
              <textarea value={data.bio} onChange={e => update('bio', e.target.value)} placeholder="What drives you as an educator? What are you working on? What would you love to collaborate on?" rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
            </div>
            <div>
              <label style={labelStyle}>Pick your avatar colour</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {AVATAR_COLORS.map(col => (
                  <button key={col} onClick={() => update('avatar_color', col)} style={{ width: 40, height: 40, background: col, border: data.avatar_color === col ? `3px solid ${C.orange}` : '3px solid transparent', cursor: 'pointer', outline: 'none', borderRadius: 0 }} />
                ))}
              </div>
            </div>
            {/* Preview */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 56, height: 56, background: data.avatar_color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 400, fontSize: 20, fontFamily: FONT.display, borderRadius: 3, flexShrink: 0, letterSpacing: '0.05em' }}>{initials}</div>
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 20, color: C.ink, letterSpacing: '0.04em', lineHeight: 1 }}>{data.name.toUpperCase() || 'YOUR NAME'}</div>
                <div style={{ color: C.orange, fontSize: 13, fontWeight: 700, fontFamily: FONT.body, marginTop: 3 }}>{data.role || 'Your role'}</div>
                <div style={{ color: C.inkLight, fontSize: 12, fontFamily: FONT.body, marginTop: 1 }}>{data.school || 'Your school'}</div>
              </div>
            </div>
            {error && <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', padding: '10px 14px', fontSize: 13, color: '#DC2626', fontFamily: FONT.body }}>{error}</div>}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 32 }}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: '14px 0', border: `1px solid ${C.border}`, background: 'transparent', color: C.inkLight, fontFamily: FONT.display, fontSize: 18, letterSpacing: '0.06em', cursor: 'pointer' }}>← BACK</button>
          )}
          {step < 3 ? (
            <button onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: '14px 0', border: 'none', background: C.orange, color: '#fff', fontFamily: FONT.display, fontSize: 18, letterSpacing: '0.06em', cursor: 'pointer' }}>CONTINUE →</button>
          ) : (
            <button onClick={save} disabled={saving} style={{ flex: 2, padding: '14px 0', border: 'none', background: saving ? C.inkFaint : C.orange, color: '#fff', fontFamily: FONT.display, fontSize: 18, letterSpacing: '0.06em', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'SAVING...' : 'ENTER EDIFYI →'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
